import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { loginWithEmail, signupWithEmail } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import './SignInModal.css';

const SignInModal = ({ isOpen, onClose }) => {
    const [isSignUp, setIsSignUp] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = isSignUp
            ? await signupWithEmail(email, password)
            : await loginWithEmail(email, password);

        setLoading(false);

        if (result.success) {
            onClose();
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
    };

    const toggleMode = () => {
        setIsSignUp(!isSignUp);
        setError('');
    };

    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const modalVariants = {
        hidden: {
            opacity: 0,
            scale: 0.8,
            y: -50,
        },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 25,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.8,
            y: 50,
            transition: {
                duration: 0.2,
            },
        },
    };

    const inputVariants = {
        focus: {
            scale: 1.02,
            transition: {
                type: 'spring',
                stiffness: 300,
                damping: 20,
            },
        },
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="modal-backdrop"
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    onClick={onClose}
                >
                    <motion.div
                        className="modal-container"
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button className="modal-close" onClick={onClose}>
                            ✕
                        </button>

                        <motion.div className="modal-header">
                            <h2>{isSignUp ? 'Create Account' : 'Welcome Back'}</h2>
                            <p>{isSignUp ? 'Sign up to get started' : 'Sign in to continue'}</p>
                        </motion.div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <motion.div className="form-group" variants={inputVariants} whileFocus="focus">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                />
                            </motion.div>

                            <motion.div className="form-group" variants={inputVariants} whileFocus="focus">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    minLength={6}
                                />
                            </motion.div>

                            {error && (
                                <motion.div
                                    className="error-message"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    {error}
                                </motion.div>
                            )}

                            <motion.button
                                type="submit"
                                className="submit-button"
                                disabled={loading}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {loading ? (
                                    <motion.div
                                        className="spinner"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    />
                                ) : (
                                    isSignUp ? 'Sign Up' : 'Sign In'
                                )}
                            </motion.button>
                        </form>

                        <div className="modal-footer">
                            <p>
                                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                                <button onClick={toggleMode} className="toggle-button">
                                    {isSignUp ? 'Sign In' : 'Sign Up'}
                                </button>
                            </p>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default SignInModal;
